import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { Button } from '../../../../components/Button/Button';
import { useAttributeCatalog } from '../../../../components/AttributeOptions/useAttributeCatalog';
import { IAttribute, IAttributeValue } from '../../attributes/constants';
import { AttributeValueOption } from './attribute.types';

interface Props {
  // Effective attribute_value ids already linked (existing + pending − removed).
  linkedValueIds: Set<number>;
  onAdd: (value: AttributeValueOption) => void;
  disabled?: boolean;
}

// Cascading attribute group → value picker to add an attribute-option link to
// the focused entity. Mirrors the affinity CategoryLinkEditor's L1→L2 cascade.
export const AttributeValueLinkEditor: React.FC<Props> = ({ linkedValueIds, onAdd, disabled }) => {
  const { attributes } = useAttributeCatalog();
  const [group, setGroup] = useState<IAttribute | null>(null);
  const [value, setValue] = useState<IAttributeValue | null>(null);

  const valueOptions = useMemo<IAttributeValue[]>(
    () => (group ? [...(group.values || [])].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [group],
  );

  const alreadyLinked = value ? linkedValueIds.has(value.id) : false;

  const handleAdd = () => {
    if (!group || !value || alreadyLinked) return;
    onAdd({ id: value.id, name: value.name, attributeId: group.id, attributeName: group.name });
    setValue(null);
  };

  return (
    <Box className="flex flex-col gap-3">
      <Typography fontWeight={600} sx={{ fontSize: 14 }}>
        Add an attribute option
      </Typography>
      <Autocomplete
        size="small"
        value={group}
        onChange={(_, v) => {
          setGroup(v);
          setValue(null);
        }}
        options={attributes}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={disabled}
        renderInput={(params) => <TextField {...params} label="Attribute group" />}
      />
      <Autocomplete
        size="small"
        value={value}
        onChange={(_, v) => setValue(v)}
        options={valueOptions}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={disabled || !group}
        renderInput={(params) => <TextField {...params} label="Option" />}
      />
      {alreadyLinked && (
        <Typography color="var(--down)" sx={{ fontSize: 12 }}>
          {value?.name} is already linked.
        </Typography>
      )}
      <Box>
        <Button onClick={handleAdd} disabled={disabled || !value || alreadyLinked} className="!px-5">
          <Typography>{value ? `Add ${value.name}` : 'Add option'}</Typography>
        </Button>
      </Box>
    </Box>
  );
};
